// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Payload sent to /device/sync on register (no user_id yet) */
export interface DeviceSyncRegisterPayload {
  device_id: string;
  platform: string;
  browser: string;
  os: string;
  device_name: string;
  timezone: string;
  app_version: string;
  product: string;
}

/** Payload sent to /device/sync on login (user_id known) */
export interface DeviceSyncLoginPayload extends DeviceSyncRegisterPayload {
  user_id: string;
}

/** Full device metadata snapshot — also used for future Active Devices UI */
export interface DeviceMetadata {
  device_id: string;
  platform: string;
  browser: string;
  os: string;
  device_name: string;
  timezone: string;
  app_version: string;
  location: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DEVICE_ID_KEY = 'device_id';
const APP_VERSION_KEY = 'app_version';
const DEVICE_PLATFORM_KEY = 'device_platform';

/** Default app version — override via localStorage if the native shell sets it */
const DEFAULT_APP_VERSION = '1.0.0';

/** Platform value for web builds */
const WEB_PLATFORM = 'web';

// ---------------------------------------------------------------------------
// Core helpers
// ---------------------------------------------------------------------------

/**
 * Returns the stored device_id or generates + stores a new one.
 * Uses crypto.randomUUID() — available in all modern browsers (Chrome 92+,
 * Firefox 95+, Safari 15.4+, Edge 92+).  Falls back to a manual UUID v4
 * generator for legacy environments.
 *
 * IMPORTANT: call this on every app boot; never clear it on logout.
 */
export function getOrCreateDeviceId(): string {
  const existing = localStorage.getItem(DEVICE_ID_KEY);
  if (existing) {
    return existing;
  }

  const newId = generateUUID();
  localStorage.setItem(DEVICE_ID_KEY, newId);
  return newId;
}

/**
 * Generates a UUID v4.
 * Prefers crypto.randomUUID(); falls back to a manual implementation so the
 * utility works in older WebViews (Android < API-level 31, older iOS WKWebView).
 */
function generateUUID(): string {
  if (
    typeof crypto !== 'undefined' &&
    typeof (crypto as Crypto).randomUUID === 'function'
  ) {
    return (crypto as Crypto).randomUUID();
  }

  // Fallback: manual UUID v4
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// ---------------------------------------------------------------------------
// Browser / OS detection
// ---------------------------------------------------------------------------

/**
 * Returns a human-readable browser name.
 *
 * Strategy (in priority order):
 *   1. navigator.userAgentData.brands — modern, NOT spoofed by DevTools
 *      responsive/device-emulation mode (Chrome 90+, Edge 90+).
 *   2. UA string parsing — fallback for Firefox, Safari, older browsers.
 */
export function getBrowserName(): string {
  // --- Priority 1: User-Agent Client Hints brands list ---
  const uaData = (navigator as any).userAgentData;
  if (uaData?.brands?.length) {
    const brands: Array<{ brand: string; version: string }> = uaData.brands;
    // Skip noise entries like "Not/A)Brand" and raw "Chromium"
    const real = brands.find(
      (b) => !/not.?a.?brand/i.test(b.brand) && !/^chromium$/i.test(b.brand)
    );
    if (real) {
      if (/edge/i.test(real.brand))       return 'Edge';
      if (/opera|opr/i.test(real.brand))  return 'Opera';
      if (/chrome/i.test(real.brand))     return 'Chrome';
      if (/firefox/i.test(real.brand))    return 'Firefox';
      if (/safari/i.test(real.brand))     return 'Safari';
    }
    // Chromium-based browser without a recognised brand name
    if (brands.some((b) => /chromium/i.test(b.brand))) return 'Chrome';
  }

  // --- Priority 2: UA string fallback (Firefox, Safari, legacy) ---
  const ua = navigator.userAgent;

  if (/Edg\//.test(ua))                               return 'Edge';
  if (/OPR\/|Opera/.test(ua))                         return 'Opera';
  if (/Chrome\//.test(ua) && !/Chromium/.test(ua))    return 'Chrome';
  if (/Firefox\//.test(ua))                           return 'Firefox';
  if (/Safari\//.test(ua) && !/Chrome/.test(ua))      return 'Safari';
  if (/MSIE|Trident/.test(ua))                        return 'IE';

  return 'Unknown Browser';
}

/**
 * Returns a human-readable OS name.
 *
 * Strategy (in priority order):
 *   1. navigator.userAgentData.platform — modern, NOT spoofed by DevTools
 *      responsive/device-emulation mode (Chrome 90+, Edge 90+).
 *      DevTools rewrites navigator.userAgent to a mobile UA but leaves
 *      userAgentData.platform untouched — this is the fix for the
 *      "Windows machine reported as Android" bug.
 *   2. UA string parsing — fallback for Firefox, Safari, older browsers.
 *
 * UA string check order is intentional:
 *   "Windows NT" and "iPhone/iPad" must be checked BEFORE "Android" and
 *   "Linux" to avoid false-positive matches on desktop UA strings.
 */
export function getOSName(): string {
  // --- Priority 1: User-Agent Client Hints platform string ---
  const uaData = (navigator as any).userAgentData;
  if (uaData?.platform) {
    const p: string = uaData.platform;
    if (/windows/i.test(p))   return 'Windows';
    if (/android/i.test(p))   return 'Android';
    if (/ios/i.test(p))       return 'iOS';
    if (/macos/i.test(p))     return 'macOS';
    if (/chromeos/i.test(p))  return 'ChromeOS';
    if (/linux/i.test(p))     return 'Linux';
  }

  // --- Priority 2: UA string fallback (Firefox, Safari, legacy) ---
  const ua = navigator.userAgent;

  if (/Windows NT/.test(ua))          return 'Windows';
  if (/iPhone|iPad|iPod/.test(ua))    return 'iOS';
  if (/Mac OS X/.test(ua))            return 'macOS';
  if (/CrOS/.test(ua))                return 'ChromeOS';
  if (/Android/.test(ua))             return 'Android';
  if (/Linux/.test(ua))               return 'Linux';

  return 'Unknown OS';
}

/**
 * Returns a combined device name string, e.g. "Chrome on Windows".
 * Displayed in the Active Devices screen.
 */
export function getDeviceName(): string {
  return `${getBrowserName()} on ${getOSName()}`;
}

/**
 * Returns the IANA timezone string, e.g. "Asia/Kolkata".
 * Safe — Intl is supported in all modern browsers.
 */
export function getTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'UTC';
  }
}

/**
 * Returns the platform string: "web" for browsers.
 * Native shells (React Native / Capacitor) should write their own value
 * ("android" | "ios") to localStorage under DEVICE_PLATFORM_KEY before the
 * Angular app boots.
 */
export function getPlatform(): string {
  return localStorage.getItem(DEVICE_PLATFORM_KEY) || WEB_PLATFORM;
}

/**
 * Returns the app version.
 * Native shells should write the real semver to localStorage under
 * APP_VERSION_KEY before the Angular app boots.
 */
export function getAppVersion(): string {
  return localStorage.getItem(APP_VERSION_KEY) || DEFAULT_APP_VERSION;
}

// ---------------------------------------------------------------------------
// Payload builders
// ---------------------------------------------------------------------------

/**
 * Builds a complete DeviceMetadata snapshot.
 * Calls getOrCreateDeviceId() — safe to call multiple times.
 */
export function buildDeviceMetadata(): DeviceMetadata {
  return {
    device_id:   getOrCreateDeviceId(),
    platform:    getPlatform(),
    browser:     getBrowserName(),
    os:          getOSName(),
    device_name: getDeviceName(),
    timezone:    getTimezone(),
    app_version: getAppVersion(),
    location: localStorage.getItem('city') || 'Unknown',
  };
}

/**
 * Builds the payload for /device/sync during registration.
 * No user_id — the account may not exist yet.
 */
export function buildRegisterDevicePayload(product: string): DeviceSyncRegisterPayload {
  return {
    ...buildDeviceMetadata(),
    product,
  };
}

/**
 * Builds the payload for /device/sync during login.
 * Includes user_id — the session has just been established.
 */
export function buildLoginDevicePayload(
  userId: string,
  product: string
): DeviceSyncLoginPayload {
  return {
    ...buildDeviceMetadata(),
    user_id: userId,
    product,
  };
}

// ---------------------------------------------------------------------------
// Session-safe logout helper
// ---------------------------------------------------------------------------

/**
 * Clears all auth-related keys from localStorage.
 * Intentionally EXCLUDES: device_id, device_platform, app_version.
 *
 * Call this in your AuthService / logout handler instead of localStorage.clear().
 * This preserves the trusted-device identity across sessions — identical
 * behaviour to WhatsApp Web and Telegram Web.
 *
 * Keys to preserve (never cleared):
 *   - device_id
 *   - device_platform
 *   - app_version
 */
export function clearAuthStorage(): void {
  const PRESERVED_KEYS: string[] = [
    DEVICE_ID_KEY,
    DEVICE_PLATFORM_KEY,
    APP_VERSION_KEY,
  ];

  const keysToRemove: string[] = [];

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && !PRESERVED_KEYS.includes(key)) {
      keysToRemove.push(key);
    }
  }

  keysToRemove.forEach((key) => localStorage.removeItem(key));
}